import styled from '@emotion/styled';
import { useState, useEffect } from 'react';
import { ChevronLeft, Search, Info, Star, RefreshCw } from 'lucide-react';
import type { CustomerSubscription } from '@/domain/entities/subscription.entity';
import type { CollectionProduct } from '@/domain/entities/collection.entity';
import { useSearch } from '@/lib/hooks/use-collections';

const DESKTOP_BREAKPOINT = '768px';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
`;

const HeaderBar = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 0 20px;
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: #292929;
  display: flex;
  align-items: center;

  &:hover {
    opacity: 0.7;
  }
`;

const PageTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  line-height: 1.4;
  color: #292929;
  margin: 0;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    font-size: 24px;
    line-height: 1.33;
  }
`;

const InfoBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  background: #d1d1d1;
  border-radius: 6px;
  padding: 12px 16px;
  margin-bottom: 16px;
`;

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  color: #292929;
  flex-shrink: 0;
`;

const InfoText = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  color: #292929;
`;

const SearchRow = styled.div`
  position: relative;
  margin-bottom: 12px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 10px 14px 10px 38px;
  border: 1px solid #292929;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.43;
  color: #292929;
  outline: none;
  box-sizing: border-box;

  &::placeholder {
    color: #292929;
  }

  &:focus {
    border-color: #292929;
  }
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  color: #292929;
  pointer-events: none;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const FilterLabel = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  color: #292929;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #292929;
  background: #f7f7f7;
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: #292929;
  }
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (min-width: ${DESKTOP_BREAKPOINT}) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(4, 1fr);
  }
`;

const ProductCard = styled.div`
  display: flex;
  flex-direction: column;
  background: #fff;
  padding: 8px;
  border-radius: 8px;
`;

const ProductImage = styled.div<{ bgUrl?: string }>`
  width: 100%;
  aspect-ratio: 3 / 4;
  background: ${({ bgUrl }) =>
    bgUrl ? `url(${bgUrl}) center/cover no-repeat` : '#f0f0f0'};
  border-radius: 10px;
`;

const ProductInfo = styled.div`
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

const ProductName = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 1.43;
  color: #292929;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const PriceRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
`;

const OriginalPrice = styled.span`
  font-size: 14px;
  font-weight: 400;
  color: #767676;
  text-decoration: line-through;
`;

const AutoshipPrice = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #292929;
`;

const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StarIcon = styled.span`
  display: flex;
  align-items: center;
  color: #fbbf24;
`;

const RatingText = styled.span`
  font-size: 12px;
  color: #292929;
`;

const SwapButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin: 4px 0 0;
  padding: 10px 0;
  border: none;
  border-radius: 8px;
  background: #292929;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #444;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #5a5a5a;
  font-size: 14px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 40px 16px;
  color: #5a5a5a;
  font-size: 14px;
`;

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

interface SwapProductBrowsePageProps {
  readonly subscription: CustomerSubscription;
  readonly onSelectProduct: (product: CollectionProduct) => void;
  readonly onBack: () => void;
}

export function SwapProductBrowsePage({
  subscription,
  onSelectProduct,
  onBack,
}: SwapProductBrowsePageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [filterBy, setFilterBy] = useState('');
  // TODO: Re-enable autoship tag filter when products are tagged: { tags: ['autoship'] }
  const { data, loading, search } = useSearch({});

  useEffect(() => {
    search({ q: searchQuery || undefined, sort: sortBy || undefined });
  }, [searchQuery, sortBy, filterBy]);

  const products = data?.products ?? [];

  return (
    <Container>
      <HeaderBar>
        <BackButton onClick={onBack} aria-label="Back to manage subscription">
          <ChevronLeft size={22} />
        </BackButton>
        <PageTitle>Autoship</PageTitle>
      </HeaderBar>

      <InfoBanner>
        <InfoIcon>
          <Info size={18} />
        </InfoIcon>
        <InfoText>
          Swapping will change your subscription
        </InfoText>
      </InfoBanner>

      <SearchRow>
        <SearchIcon>
          <Search size={16} />
        </SearchIcon>
        <SearchInput
          type="text"
          placeholder="Search for products"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search for products"
        />
      </SearchRow>

      <FilterRow>
        <FilterGroup>
          <FilterLabel>Filter by</FilterLabel>
          <FilterSelect
            value={filterBy}
            onChange={(e) => setFilterBy(e.target.value)}
            aria-label="Filter by"
          >
            <option value="">Select filter</option>
            <option value="bras">Bras</option>
            <option value="shapewear">Shapewear</option>
            <option value="underwear">Underwear</option>
            <option value="leggings">Leggings</option>
          </FilterSelect>
        </FilterGroup>
        <FilterGroup>
          <FilterLabel>Sort by</FilterLabel>
          <FilterSelect
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            aria-label="Sort by"
          >
            <option value="">Featured</option>
            <option value="price_asc">Price: Low to High</option>
            <option value="price_desc">Price: High to Low</option>
            <option value="title_asc">Name: A-Z</option>
            <option value="title_desc">Name: Z-A</option>
          </FilterSelect>
        </FilterGroup>
      </FilterRow>

      {loading && <LoadingState>Loading products...</LoadingState>}

      {!loading && products.length === 0 && (
        <EmptyState>No products found. Try adjusting your search.</EmptyState>
      )}

      {!loading && products.length > 0 && (
        <ProductGrid>
          {products.map((product) => (
            <ProductCard key={product.id}>
              <ProductImage bgUrl={product.imageUri} role="img" aria-label={product.title} />
              <ProductInfo>
                <ProductName>{product.title}</ProductName>
                <PriceRow>
                  {product.priceMax > product.priceMin && (
                    <OriginalPrice>{formatCents(product.priceMax)}</OriginalPrice>
                  )}
                  <AutoshipPrice>{formatCents(product.priceMin)}</AutoshipPrice>
                </PriceRow>
                <RatingRow>
                  <StarIcon><Star size={12} fill="#fbbf24" stroke="#fbbf24" /></StarIcon>
                  <RatingText>4.5</RatingText>
                </RatingRow>
              </ProductInfo>
              <SwapButton
                type="button"
                onClick={() => onSelectProduct(product)}
                aria-label={`Swap to ${product.title}`}
              >
                <RefreshCw size={16} />
                Swap
              </SwapButton>
            </ProductCard>
          ))}
        </ProductGrid>
      )}
    </Container>
  );
}
